import format from 'date-fns/format'
import { Circle } from 'react-feather'

import { Tooltip, useGlobalState } from '@pepper/ui'
import { pluralize } from '@pepper/utils/src/function/pluralize'
import getProgressBarPoints from '@pepper/utils/src/function/getProgressBarPoints'
import { Domain } from '@pepper/utils/src/types/seo'

import SpacedProgressBar from 'components/ProgressBar/SpacedProgressBar'
import DraftDropdown from 'components/DraftDropdown/DraftDropdown'
import ProjectDropdown from 'components/ProjectDropdown/ProjectDropdown'
import DomainPill from 'components/DomainPill'

import s from './_.module.css'

interface ProjectHorizontalCardProps {
  project: Project
  isDraft: boolean
  stage_count: { [key in AssignmentStage]?: number }
  topicsCount?: number
  domain?: Domain
}

const ProjectHorizontalCard = ({
  project,
  isDraft,
  stage_count,
  topicsCount,
  domain,
}: ProjectHorizontalCardProps): JSX.Element => {
  const {
    created_at: dateCreated,
    project_title,
    project_code,
    number_of_assignments,
    number_of_project_drafts: draft_count,
    has_pending_draft_items,
    id: project_id,
    number_of_items,
  } = project

  const isPaidDraft = project.payment_status === 'payment.captured'

  const { permissions } = useGlobalState()
  const hasMarkpetplaceSubscription = permissions?.marketplace
  const assignmentCounts = getProgressBarPoints(stage_count)

  const projectCode = project_code ? `#${project_code}` : ''

  const showProgressBar = hasMarkpetplaceSubscription
    ? true
    : !!number_of_assignments

  return (
    <section className={s.horizontalCard}>
      <div className={s.horizontalCardContainer}>
        <div className={s.contentContainer}>
          <span className={s.lightGray}>{projectCode}</span>
          <div className={s.fixWidth} title={project_title}>
            {project_title ?? 'N/A'}
          </div>
          {domain && (
            <>
              <div className={s.dot}>•</div>
              <DomainPill domain={domain} />
            </>
          )}
        </div>
        <div className={s.detailsContainer}>
          {(has_pending_draft_items || isDraft) && draft_count > 0 && (
            <span className={s.hasDraft}>
              {pluralize(draft_count, 'Draft')}
            </span>
          )}
          <div>
            <Tooltip
              placement="bottomLeft"
              overlayClassName={s.tooltip}
              title={
                !!number_of_items && (
                  <div className={s.tooltipInner}>
                    <span>
                      <Circle className={s.circleGreen} size={8} />
                      Approved ({assignmentCounts.completedCount})
                    </span>
                    <span>
                      <Circle className={s.circleYellow} size={8} />
                      Approval Pending ({assignmentCounts.pendingCount})
                    </span>
                    <span>
                      <Circle className={s.circleCyan} size={8} />
                      New & In Progress ({assignmentCounts.newInProgressCount})
                    </span>
                  </div>
                )
              }
            >
              {showProgressBar && (
                <div className={s.progressContainer}>
                  {number_of_items ? (
                    <SpacedProgressBar count={stage_count} />
                  ) : (
                    <div className={s.zeroCountProgress} />
                  )}
                  {pluralize(number_of_items, 'Item')}
                </div>
              )}
            </Tooltip>
          </div>
          {domain && <div className={s.topicsCount}>{topicsCount} Topics</div>}
          <div className={s.createdOn}>
            {format(new Date(dateCreated), 'do MMM')}
          </div>
          <button
            className={s.btnContainer}
            onClick={event => event.stopPropagation()}
          >
            {isDraft ? (
              <DraftDropdown projectId={project_id} isPaidDraft={isPaidDraft} />
            ) : (
              <ProjectDropdown
                icon="more"
                projectName={project_title}
                projectId={project_id}
                projectStatus={status}
              />
            )}
          </button>
        </div>
      </div>
    </section>
  )
}

export default ProjectHorizontalCard
